import { SL_WORKSPACE_PROFILE } from '@app/sl-workspace/profile';
import FaviconSvg from '@icon/macro-logo.svg?raw';
import FaviconBadgeSvg from '@icon/macro-logo-badge.svg?raw';
import SlWorkspaceLogoUrl from '@icon/sl-workspace-logo.png?inline';

const FAVICON_SIZE = 48;

let currentFaviconLink: HTMLLinkElement | null = null;

/** escapes a color value for use in SVG */
function escapeColorForSvg(color: string): string {
  return color.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** insert color and url encode SVG */
function processSvg(svg: string, color: string) {
  return `data:image/svg+xml,${encodeURIComponent(svg.replace(/currentColor/g, escapeColorForSvg(color)))}`;
}

function faviconSource(color: string, hasBadge = false): string {
  if (SL_WORKSPACE_PROFILE.enabled) return SlWorkspaceLogoUrl;
  return processSvg(hasBadge ? FaviconBadgeSvg : FaviconSvg, color);
}

/**
 * Return a data URL for the active product mark.
 */
export function getFaviconUrl(color: string) {
  return faviconSource(color);
}

/**
 * Update the site's live favicon with a new color, and optionally a notification
 * badge with its own color.
 */
export function updateFavicon(
  faviconColor: string,
  badgeColor?: string,
  hasBadge?: boolean
): void {
  if (currentFaviconLink?.parentNode) {
    currentFaviconLink.parentNode.removeChild(currentFaviconLink);
    currentFaviconLink = null;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = FAVICON_SIZE;
  canvas.height = FAVICON_SIZE;

  const img = new Image();
  img.src = faviconSource(faviconColor, hasBadge);

  img.onload = () => {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    if (hasBadge) {
      const badgeRadius = 6;

      // The upstream Macro badge SVG already reserves this space. The SL
      // source is a raster mark, so clear an equivalent halo before painting
      // the notification badge to preserve its silhouette at 16-48 px.
      if (SL_WORKSPACE_PROFILE.enabled) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(
          canvas.width - badgeRadius,
          badgeRadius,
          badgeRadius + 1,
          0,
          2 * Math.PI
        );
        ctx.fill();
        ctx.restore();
      }

      ctx.beginPath();
      ctx.arc(
        canvas.width - badgeRadius,
        badgeRadius,
        badgeRadius,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = badgeColor || faviconColor;
      ctx.fill();
    }

    const faviconUrl = canvas.toDataURL();

    if (currentFaviconLink?.parentNode) {
      currentFaviconLink.parentNode.removeChild(currentFaviconLink);
    }

    const existingLinks = document.querySelectorAll('link[rel*="icon"]');
    existingLinks.forEach((link) => {
      link.remove();
    });

    // create and add new favicon
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = faviconUrl;
    document.head.appendChild(link);
    currentFaviconLink = link;

    // update existing shortcut icon if present
    const existingShortcutIcon = document.querySelector(
      'link[rel="shortcut icon"]'
    ) as HTMLLinkElement;
    if (existingShortcutIcon) {
      existingShortcutIcon.href = faviconUrl;
    }
  };
}
